<template>
  <div v-if="show" class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3>{{ brokerName }} Credentials</h3>
        <button @click="closeModal" class="close-button">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"/>
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <p class="modal-description">
          {{ modalDescription }}
        </p>

        <div v-if="hasStoredCredentials && !isEditing" class="stored-credentials-section primary">
          <div class="stored-credentials-header">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 16A8 8 0 108 0a8 8 0 000 16zm3.78-9.72a.75.75 0 00-1.06-1.06L7 8.94 5.28 7.22a.75.75 0 00-1.06 1.06l2.25 2.25c.293.293.767.293 1.06 0l4.25-4.25z"/>
            </svg>
            <span>Saved Credentials Loaded</span>
          </div>
          <p class="stored-credentials-info">
            {{ apiKeyLabel }}: <strong>{{ storedApiKey || 'Saved' }}</strong>
          </p>
          <div class="stored-credentials-actions">
            <button
              type="button"
              @click="useStoredCredentials"
              class="use-stored-button"
              :disabled="isLoading"
            >
              Use Saved Credentials
            </button>
            <button
              type="button"
              @click="startEditing"
              class="edit-stored-button"
              :disabled="isLoading"
            >
              Change Credentials
            </button>
          </div>
        </div>

        <form v-if="isEditing" @submit.prevent="saveCredentials">
          <div v-if="brokerType === 'dhan'" class="form-group">
            <label for="dhanClientId">
              Dhan Client ID *
            </label>
            <input
              id="dhanClientId"
              v-model="credentials.dhanClientId"
              type="text"
              placeholder="Enter your Dhan client id"
              class="form-input"
              required
            />
          </div>

          <div class="form-group">
            <label for="apiKey">
              {{ apiKeyLabel }} *
            </label>
            <input
              id="apiKey"
              v-model="credentials.apiKey"
              type="text"
              :placeholder="'Enter ' + brokerName + ' ' + apiKeyLabel"
              class="form-input"
              required
            />
          </div>

          <div class="form-group">
            <label for="apiSecret">
              API Secret {{ hasStoredCredentials ? '(leave blank to keep saved secret)' : '*' }}
            </label>
            <input
              id="apiSecret"
              v-model="credentials.apiSecret"
              type="password"
              :placeholder="hasStoredCredentials ? 'Leave blank to keep current secret' : 'Enter ' + brokerName + ' API Secret'"
              class="form-input"
              :required="!hasStoredCredentials"
            />
          </div>

          <div v-if="error" class="error-message">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 16A8 8 0 108 0a8 8 0 000 16zM5.354 4.646a.5.5 0 10-.708.708L7.293 8l-2.647 2.646a.5.5 0 00.708.708L8 8.707l2.646 2.647a.5.5 0 00.708-.708L8.707 8l2.647-2.646a.5.5 0 00-.708-.708L8 7.293 5.354 4.646z"/>
            </svg>
            <span>{{ error }}</span>
          </div>

          <div class="modal-actions">
            <button
              type="button"
              @click="cancelEditing"
              class="cancel-button"
              :disabled="isLoading"
            >
              {{ hasStoredCredentials ? 'Back' : 'Cancel' }}
            </button>
            <button
              type="submit"
              class="save-button"
              :disabled="isLoading || !credentials.apiKey || (brokerType === 'dhan' && !credentials.dhanClientId) || (!hasStoredCredentials && !credentials.apiSecret)"
            >
              <span v-if="isLoading" class="btn-loading">
                <svg class="spinner" width="16" height="16" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="31.416" stroke-dashoffset="31.416">
                    <animate attributeName="stroke-dashoffset" dur="0.6s" repeatCount="indefinite" from="31.416" to="0"/>
                  </circle>
                </svg>
                Saving...
              </span>
              <span v-else>{{ hasStoredCredentials ? 'Update Credentials' : 'Save Credentials' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BrokerCredentialsModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    brokerType: {
      type: String,
      required: true,
      validator: value => ['fyers', 'upstox', 'aliceblue', 'dhan'].includes(value)
    },
    hasStoredCredentials: {
      type: Boolean,
      default: false
    },
    storedApiKey: {
      type: String,
      default: ''
    },
    storedDhanClientId: {
      type: String,
      default: ''
    },
    startInEdit: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    brokerName() {
      const names = {
        fyers: 'Fyers',
        upstox: 'Upstox',
        aliceblue: 'AliceBlue',
        dhan: 'DhanHQ'
      };
      return names[this.brokerType] || 'Broker';
    },
    apiKeyLabel() {
      const labels = {
        fyers: 'App ID',
        upstox: 'API Key',
        aliceblue: 'App Code',
        dhan: 'API Key'
      };
      return labels[this.brokerType] || 'API Key';
    },
    modalDescription() {
      if (this.hasStoredCredentials && !this.isEditing) {
        return `Use your saved ${this.brokerName} credentials, or update them if your app details changed.`;
      }
      if (this.hasStoredCredentials) {
        return `Update your ${this.brokerName} ${this.apiKeyLabel}. Enter a new secret only if you want to replace the saved one.`;
      }
      return `Enter your ${this.brokerName} API credentials. These will be stored securely and used for future connections.`;
    }
  },
  data() {
    return {
      isLoading: false,
      error: null,
      isEditing: false,
      credentials: {
        apiKey: '',
        apiSecret: '',
        dhanClientId: ''
      }
    };
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
    startInEdit(newVal) {
      if (this.show && newVal) {
        this.startEditing();
      }
    }
  },
  methods: {
    resetForm() {
      this.credentials = {
        apiKey: this.storedApiKey || '',
        apiSecret: '',
        dhanClientId: this.storedDhanClientId || ''
      };
      this.error = null;
      this.isLoading = false;
      this.isEditing = this.startInEdit || !this.hasStoredCredentials;
    },

    closeModal() {
      if (!this.isLoading) {
        this.$emit('close');
      }
    },

    async saveCredentials() {
      this.isLoading = true;
      this.error = null;

      try {
        await new Promise((resolve, reject) => {
          this.$emit('save', {
            brokerType: this.brokerType,
            credentials: {
              apiKey: this.credentials.apiKey.trim(),
              apiSecret: this.credentials.apiSecret.trim(),
              dhanClientId: this.credentials.dhanClientId.trim()
            }
          }, { resolve, reject });
        });
        
        this.$emit('close');
      } catch (error) {
        this.error = error.message || 'Failed to save credentials';
      } finally {
        this.isLoading = false;
      }
    },

    useStoredCredentials() {
      this.$emit('use-stored', this.brokerType);
      this.$emit('close');
    },

    startEditing() {
      this.credentials = {
        apiKey: this.storedApiKey || '',
        apiSecret: '',
        dhanClientId: this.storedDhanClientId || ''
      };
      this.error = null;
      this.isEditing = true;
    },

    cancelEditing() {
      if (this.hasStoredCredentials) {
        this.resetForm();
      } else {
        this.closeModal();
      }
    }
  }
};
</script>

<style scoped>
/* Theme-aware: uses global CSS variables defined in App.vue (`[data-theme="dark"]`).
   Light and dark modes follow the app's theme toggle automatically. */

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  backdrop-filter: blur(4px);
}

.modal-container {
  background: var(--tc-card-bg);
  color: var(--tc-text-primary);
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--tc-shadow-lg);
  border: 1px solid var(--tc-card-border);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 16px;
  border-bottom: 1px solid var(--tc-border-primary);
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--tc-text-primary);
}

.close-button {
  background: none;
  border: none;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--tc-text-secondary);
  transition: all 0.2s ease;
}

.close-button:hover {
  background: var(--tc-bg-hover);
  color: var(--tc-text-primary);
}

.modal-body {
  padding: 24px;
}

.modal-description {
  margin: 0 0 20px;
  font-size: 14px;
  color: var(--tc-text-secondary);
  line-height: 1.5;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--tc-text-primary);
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  background: var(--tc-input-bg);
  color: var(--tc-input-text);
  border: 2px solid var(--tc-input-border);
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  transition: all 0.2s ease;
}

.form-input::placeholder {
  color: var(--tc-input-placeholder);
}

.form-input:focus {
  outline: none;
  border-color: var(--tc-accent);
  box-shadow: 0 0 0 3px var(--tc-accent-bg);
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: var(--tc-red-bg);
  color: var(--tc-red);
  border: 1px solid var(--tc-red);
  border-radius: 8px;
  font-size: 14px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.cancel-button {
  flex: 1;
  padding: 12px 20px;
  background: var(--tc-bg-secondary);
  color: var(--tc-text-secondary);
  border: 2px solid var(--tc-border-primary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.cancel-button:hover:not(:disabled) {
  background: var(--tc-bg-hover);
  border-color: var(--tc-border-hover);
  color: var(--tc-text-primary);
}

.save-button {
  flex: 2;
  padding: 12px 20px;
  background: var(--tc-accent);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.save-button:hover:not(:disabled) {
  background: var(--tc-accent-hover);
  transform: translateY(-1px);
  box-shadow: var(--tc-shadow-md);
}

.save-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-loading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.stored-credentials-section {
  padding: 16px;
  background: var(--tc-bg-secondary);
  border: 1px solid var(--tc-border-primary);
  border-radius: 8px;
  margin-bottom: 20px;
}

.stored-credentials-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--tc-accent);
}

.stored-credentials-info {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--tc-text-secondary);
  line-height: 1.4;
}

.stored-credentials-info strong {
  color: var(--tc-text-primary);
}

.stored-credentials-actions {
  display: flex;
  gap: 10px;
}

.use-stored-button,
.edit-stored-button {
  flex: 1;
  padding: 10px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.use-stored-button {
  background: var(--tc-accent-bg);
  color: var(--tc-accent);
  border: 1px solid var(--tc-accent);
}

.edit-stored-button {
  background: var(--tc-bg-secondary);
  color: var(--tc-text-primary);
  border: 1px solid var(--tc-border-primary);
}

.use-stored-button:hover:not(:disabled) {
  background: var(--tc-accent);
  color: #ffffff;
}

.edit-stored-button:hover:not(:disabled) {
  background: var(--tc-bg-hover);
  border-color: var(--tc-border-hover);
}
</style>
